/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeField,
  DimeFieldContent,
  DimeFieldDescription,
  DimeFieldLabel,
  DimeFieldLegend,
  DimeFieldSet,
  DimeFieldTitle,
  DimeRadioGroup,
  DimeRadioGroupItem,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/radio-group')({
  component: RadioGroupDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-start gap-6 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

const PLAN_OPTIONS = [
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
];

function RadioGroupDocs() {
  const [invalidValue, setInvalidValue] = React.useState('');

  return (
    <div className="max-w-3xl">
      <h1 className="mb-1 text-3xl font-bold">Radio Group</h1>
      <p className="mb-8 text-muted-foreground">
        A set of checkable buttons where no more than one can be checked at a time. Use{' '}
        <code className="font-mono text-sm">value</code> /{' '}
        <code className="font-mono text-sm">defaultValue</code> and{' '}
        <code className="font-mono text-sm">onValueChange</code> to control state. Arrow keys move
        selection.
      </p>

      {/* Default */}
      <DocsSection title="Default">
        <Preview>
          <DimeRadioGroup defaultValue="pro" aria-label="Plan">
            {PLAN_OPTIONS.map(({ value, label }) => (
              <div key={value} className="flex items-center gap-3">
                <DimeRadioGroupItem value={value} id={`default-${value}`} />
                <label htmlFor={`default-${value}`} className="text-sm">
                  {label}
                </label>
              </div>
            ))}
          </DimeRadioGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">defaultValue</code> for uncontrolled initial selection.
          Pair each <code className="font-mono">DimeRadioGroupItem</code> with a{' '}
          <code className="font-mono">{'<label>'}</code> using matching{' '}
          <code className="font-mono">id</code> / <code className="font-mono">htmlFor</code>.
        </p>
      </DocsSection>

      {/* Description */}
      <DocsSection title="Description">
        <Preview>
          <DimeRadioGroup defaultValue="pro" aria-label="Plan with description">
            {[
              { value: 'starter', label: 'Starter', desc: 'For individuals and small teams.' },
              { value: 'pro', label: 'Pro', desc: 'Unlimited seats and advanced features.' },
              {
                value: 'enterprise',
                label: 'Enterprise',
                desc: 'Custom SLAs and dedicated support.',
              },
            ].map(({ value, label, desc }) => (
              <div key={value} className="flex items-start gap-3">
                <DimeRadioGroupItem value={value} id={`desc-${value}`} className="mt-0.5" />
                <div className="flex flex-col gap-0.5">
                  <DimeFieldLabel htmlFor={`desc-${value}`} className="font-medium">
                    {label}
                  </DimeFieldLabel>
                  <DimeFieldDescription>{desc}</DimeFieldDescription>
                </div>
              </div>
            ))}
          </DimeRadioGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Wrap each item in a <code className="font-mono">DimeField</code> with{' '}
          <code className="font-mono">DimeFieldLabel</code> and{' '}
          <code className="font-mono">DimeFieldDescription</code> for richer labels.
        </p>
      </DocsSection>

      {/* Choice Card */}
      <DocsSection title="Choice Card">
        <Preview>
          <DimeRadioGroup defaultValue="pro" aria-label="Plan choice card" className="max-w-sm">
            {[
              { value: 'starter', label: 'Starter', desc: 'For individuals and small teams.' },
              { value: 'pro', label: 'Pro', desc: 'Unlimited seats and advanced features.' },
              {
                value: 'enterprise',
                label: 'Enterprise',
                desc: 'Custom SLAs and dedicated support.',
              },
            ].map(({ value, label, desc }) => (
              <DimeFieldLabel key={value} htmlFor={`card-${value}`}>
                <DimeField orientation="horizontal">
                  <DimeFieldContent>
                    <DimeFieldTitle id={`card-title-${value}`}>{label}</DimeFieldTitle>
                    <DimeFieldDescription>{desc}</DimeFieldDescription>
                  </DimeFieldContent>
                  <DimeRadioGroupItem
                    value={value}
                    id={`card-${value}`}
                    aria-labelledby={`card-title-${value}`}
                  />
                </DimeField>
              </DimeFieldLabel>
            ))}
          </DimeRadioGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Wrap <code className="font-mono">DimeField</code> inside{' '}
          <code className="font-mono">DimeFieldLabel</code> to make the entire card clickable. Use{' '}
          <code className="font-mono">DimeFieldContent</code> +{' '}
          <code className="font-mono">DimeFieldTitle</code> /{' '}
          <code className="font-mono">DimeFieldDescription</code> for the label text, and place{' '}
          <code className="font-mono">DimeRadioGroupItem</code> at the trailing edge.
        </p>
      </DocsSection>

      {/* Fieldset */}
      <DocsSection title="Fieldset">
        <Preview>
          <DimeFieldSet aria-label="Notification preference">
            <DimeFieldLegend>Notification preference</DimeFieldLegend>
            <DimeFieldDescription>Choose how you want to receive updates.</DimeFieldDescription>
            <DimeRadioGroup defaultValue="email" name="notifications">
              {[
                { value: 'email', label: 'Email' },
                { value: 'sms', label: 'SMS' },
                { value: 'push', label: 'Push notification' },
              ].map(({ value, label }) => (
                <div key={value} className="flex items-center gap-3">
                  <DimeRadioGroupItem value={value} id={`fs-${value}`} />
                  <label htmlFor={`fs-${value}`} className="text-sm">
                    {label}
                  </label>
                </div>
              ))}
            </DimeRadioGroup>
          </DimeFieldSet>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeFieldSet</code> and{' '}
          <code className="font-mono">DimeFieldLegend</code> to group radio items with a label and
          description. Pass <code className="font-mono">name</code> on the group for form
          submission.
        </p>
      </DocsSection>

      {/* Disabled */}
      <DocsSection title="Disabled">
        <Preview>
          <DimeRadioGroup defaultValue="pro" disabled aria-label="Disabled plan">
            {PLAN_OPTIONS.map(({ value, label }) => (
              <div key={value} className="flex items-center gap-3">
                <DimeRadioGroupItem value={value} id={`disabled-${value}`} />
                <label htmlFor={`disabled-${value}`} className="text-sm text-muted-foreground">
                  {label}
                </label>
              </div>
            ))}
          </DimeRadioGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">disabled</code> on{' '}
          <code className="font-mono">DimeRadioGroup</code> to disable all items at once.
        </p>
      </DocsSection>

      {/* Invalid */}
      <DocsSection title="Invalid">
        <Preview>
          <DimeFieldSet className="w-full max-w-xs">
            <DimeFieldLegend>Plan</DimeFieldLegend>
            <DimeFieldDescription>Choose how you want to receive updates.</DimeFieldDescription>
            <DimeRadioGroup value={invalidValue} onValueChange={setInvalidValue}>
              {PLAN_OPTIONS.map(({ value, label }) => (
                <DimeField
                  key={value}
                  orientation="horizontal"
                  data-invalid={!invalidValue || undefined}
                >
                  <DimeRadioGroupItem
                    value={value}
                    id={`invalid-${value}`}
                    aria-invalid={!invalidValue || undefined}
                  />
                  <DimeFieldLabel htmlFor={`invalid-${value}`} className="font-normal">
                    {label}
                  </DimeFieldLabel>
                </DimeField>
              ))}
            </DimeRadioGroup>
          </DimeFieldSet>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Add <code className="font-mono">aria-invalid</code> to each{' '}
          <code className="font-mono">DimeRadioGroupItem</code> and{' '}
          <code className="font-mono">data-invalid</code> to each wrapping{' '}
          <code className="font-mono">DimeField</code> to show the invalid state.
        </p>
      </DocsSection>

      {/* Props */}
      <DocsSection title="Props">
        <h3 className="mb-2 text-base font-medium">DimeRadioGroup</h3>
        <table className="mb-6 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Prop</th>
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              { prop: 'value', type: 'string', desc: 'Controlled selected value.' },
              { prop: 'defaultValue', type: 'string', desc: 'Initial value (uncontrolled).' },
              {
                prop: 'onValueChange',
                type: '(value: string) => void',
                desc: 'Fires when selection changes.',
              },
              { prop: 'disabled', type: 'boolean', desc: 'Disables all items in the group.' },
              { prop: 'name', type: 'string', desc: 'Form field name for submission.' },
              { prop: 'required', type: 'boolean', desc: 'Marks the group as required.' },
              {
                prop: 'orientation',
                type: '"horizontal" | "vertical"',
                desc: 'Layout direction (also affects arrow-key navigation).',
              },
              {
                prop: 'loop',
                type: 'boolean',
                desc: 'Whether keyboard navigation wraps around. Default true.',
              },
            ].map((row) => (
              <tr key={row.prop} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{row.prop}</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">{row.type}</td>
                <td className="py-2 text-muted-foreground">{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 className="mb-2 text-base font-medium">DimeRadioGroupItem</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Prop</th>
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              {
                prop: 'value',
                type: 'string',
                desc: 'The value submitted when this item is selected.',
              },
              { prop: 'disabled', type: 'boolean', desc: 'Disables this individual item.' },
            ].map((row) => (
              <tr key={row.prop} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{row.prop}</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">{row.type}</td>
                <td className="py-2 text-muted-foreground">{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>
    </div>
  );
}
